import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../../services/supabaseClient'
import styles from './AdminPanel.module.css'

interface Team {
  id: string
  name: string
}

interface RegistrationCode {
  id: string
  code: string
  created_at: string
  expires_at: string
  used_by: string | null
  used_at: string | null
  team_id: string | null
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

function isExpired(iso: string): boolean {
  return new Date(iso).getTime() < Date.now()
}

export default function RegistrationCodes() {
  const [teams, setTeams] = useState<Team[]>([])
  const [codes, setCodes] = useState<RegistrationCode[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedTeamId, setSelectedTeamId] = useState<string>('')
  const [generating, setGenerating] = useState(false)
  const [status, setStatus] = useState<string>('')
  const [copied, setCopied] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const [{ data: teamRows }, { data: codeRows }] = await Promise.all([
      supabase.from('teams').select('id, name').eq('status', 'active').order('name'),
      supabase.from('registration_codes').select('*').order('created_at', { ascending: false }),
    ])
    setTeams((teamRows as Team[]) ?? [])
    setCodes((codeRows as RegistrationCode[]) ?? [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const handleGenerate = async () => {
    setGenerating(true)
    setStatus('')
    const { error } = await supabase.rpc('create_registration_code', {
      p_team_id: selectedTeamId || null,
      p_expires_at: null,
    })
    setGenerating(false)
    if (error) {
      setStatus(`Error: ${error.message}`)
      return
    }
    setStatus('Code generated.')
    setTimeout(() => setStatus(''), 3000)
    await load()
  }

  const handleCopy = async (code: string) => {
    await navigator.clipboard.writeText(code)
    setCopied(code)
    setTimeout(() => setCopied(null), 2000)
  }

  const teamName = (id: string | null): string => {
    if (!id) return '— Any team —'
    return teams.find((t) => t.id === id)?.name ?? 'Unknown team'
  }

  const codeStatus = (c: RegistrationCode): string => {
    if (c.used_at) return 'Used'
    if (isExpired(c.expires_at)) return 'Expired'
    return 'Available'
  }

  if (loading) {
    return (
      <div className={styles.card} data-testid="registration-codes-loading">
        <div className={styles.hdrDark}>Registration Codes</div>
        <div className={styles.loadingText}>Loading...</div>
      </div>
    )
  }

  return (
    <div className={styles.card} data-testid="registration-codes">
      <div className={styles.hdrDark}>Registration Codes</div>

      <div className={styles.bulkRow}>
        <span className={styles.bulkLabel}>Generate code for:</span>
        <select
          className={styles.inlineSelect}
          value={selectedTeamId}
          onChange={(e) => setSelectedTeamId(e.target.value)}
          data-testid="registration-code-team-select"
        >
          <option value="">— Any team —</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <button
          className={styles.btnPrimary}
          onClick={handleGenerate}
          disabled={generating}
          data-testid="generate-registration-code-btn"
        >
          {generating ? 'Generating...' : 'Generate'}
        </button>
        {status && <span className={styles.bulkLabel} data-testid="registration-code-status">{status}</span>}
      </div>

      {codes.length === 0 ? (
        <div className={styles.loadingText} data-testid="registration-codes-empty">
          No codes yet. Generate one above.
        </div>
      ) : (
        <table className={styles.table} data-testid="registration-codes-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Team</th>
              <th>Expires</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((c) => (
              <tr key={c.id} data-testid={`registration-code-row-${c.id}`}>
                <td><code>{c.code}</code></td>
                <td>{teamName(c.team_id)}</td>
                <td>{formatDate(c.expires_at)}</td>
                <td>{codeStatus(c)}</td>
                <td className={styles.actionCell}>
                  {!c.used_at && !isExpired(c.expires_at) && (
                    <button
                      className={styles.btnSm}
                      onClick={() => handleCopy(c.code)}
                      data-testid={`copy-registration-code-${c.id}`}
                    >
                      {copied === c.code ? 'Copied!' : 'Copy'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
